import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { focusManager, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { Alert, Colors } from "swing-components";

import {
  ContentSingleColumn,
  ExpiringDocumentsBanner,
  NonSwingCertCard,
  NonSwingCertEmptyView,
  OpeningsCard,
  useAuth,
} from "~components";
import { GET, getFeatureFlags, OpeningsData, PendingConfirmData, translate } from "~utils";

import { createRequestUrl } from "../Routes";
import { ScorePage } from "../ScoreTemplates/ScorePage";
import { PullToRefreshLoadingIndicator } from "./PullToRefreshLoadingIndicator";

export function Requests() {
  /********* Queries ********/
  const {
    data: openings,
    isLoading: isOpeningsLoading,
    // isSuccess: isOpeningsSuccess,
    refetch: refetchOpenings,
  } = useQuery({
    queryKey: ["fetchOpenings"],
    queryFn: () => GET("/api/sub/request/openings"),
    throwOnError: true, // flag necessary for uncaught errors to bubble to App level ErrorBoundary
  });

  const {
    data: confirmRequests,
    isLoading: isConfirmPendingLoading,
    // isSuccess: isConfirmPendingSuccess,
    refetch: refetchConfirmRequests,
  } = useQuery({
    queryKey: ["fetchPendingConfirm"],
    queryFn: () => GET("/api/sub/request/pending-confirm"),
    throwOnError: true, // flag necessary for uncaught errors to bubble to App level ErrorBoundary
  });

  const FOUR_HOURS_IN_MS = 1000 * 60 * 60 * 4;
  const { data: expiringDocuments, isLoading: isExpiringDocumentsLoading } = useQuery({
    queryKey: ["fetchExpiringDocuments"],
    queryFn: () => GET("/api/sub/documents/expiring"),
    // Cache for 4 hours
    staleTime: FOUR_HOURS_IN_MS,
    // Drop from cache after 4 hours if unused or inactive
    gcTime: FOUR_HOURS_IN_MS,
  });

  /********* Hooks ********/
  const [lastUpdatedTime, setLastUpdatedTime] = useState(DateTime.now());
  // Update the lastUpdatedTime when the app regains focus.
  // refetchOnWindowFocus is triggered automatically on web and mobile devices,
  // but we also need to update the time to make sure it's in sync.
  // If an error is thrown during the fetch, the timestamp will update,
  // but the UI will not show the time as it will be covered by the error <Alert/>
  useEffect(() => {
    const unsubscribe = focusManager.subscribe(async (isFocused) => {
      if (!isFocused) return;

      setLastUpdatedTime(DateTime.now());
      // If UI requirements change, and the timestamp should only update on successful data fetch:
      // Uncomment the following logic to manually refetch and update the time
      // only when the requests succeed. At this time, we will need to consider whether
      // refetchOnWindowFocus should be set to false.

      // const [openingsResult, confirmResult] = await Promise.all([
      //   refetchOpenings(),
      //   refetchConfirmRequests(),
      // ]);

      // if (openingsResult.status === "success" || confirmResult.status === "success") {
      //   setLastUpdatedTime(DateTime.now());
      // }
    });
    return () => unsubscribe();
  }, []);

  const hasExpiringDocuments = expiringDocuments?.data.expiringDocuments;

  return (
    <ScorePage
      title={translate("PAGE_TITLE_OPENINGS")}
      isLoading={isOpeningsLoading || isConfirmPendingLoading || isExpiringDocumentsLoading}
      hasTopBanner={hasExpiringDocuments}
    >
      <ContentSingleColumn>
        {hasExpiringDocuments && <ExpiringDocumentsBanner />}
        {/* TODO update with conditional for no data state when updating openings page */}
        {openings?.data && (
          <RequestsView
            requests={openings.data}
            confirmRequests={confirmRequests?.data}
            lastUpdatedTime={lastUpdatedTime}
            onRefresh={() => {
              setLastUpdatedTime(DateTime.now());
              refetchOpenings();
              refetchConfirmRequests();
            }}
          />
        )}
      </ContentSingleColumn>
    </ScorePage>
  );
}

export type RequestsViewProps = {
  requests: OpeningsData[];
  confirmRequests?: PendingConfirmData[];
  lastUpdatedTime: DateTime;
  onRefresh: () => void;
};

export function RequestsView(props: RequestsViewProps) {
  const { requests = [], confirmRequests, onRefresh, lastUpdatedTime } = props;

  /********* Hooks ********/
  const {
    userInfo: {
      isPhoneNumberVerified,
      notificationsSettings,
      hasSwingCert,
      isSwingCertRequestsAvailable,
      isActivated,
      address,
    },
  } = useAuth();
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(0);
  const isPayClarityEnabled = getFeatureFlags().W2_PAY_CLARITY;

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = DateTime.now();
      setTimeSinceUpdate(Math.floor(now.diff(lastUpdatedTime, "minutes").minutes));
    }, 60 * 1000); // Update every minute

    // Initial update
    setTimeSinceUpdate(Math.floor(DateTime.now().diff(lastUpdatedTime, "minutes").minutes));

    return () => clearInterval(intervalId);
  }, [lastUpdatedTime]);

  /********* State ********/
  const [shouldHideSwingCertCard, setShouldHideSwingCertCard] = useState(
    () => window.localStorage.getItem("hideSwingCertCard") === "true",
  );

  /********* Constants ********/
  const showPhoneVerificationAlert = isPhoneNumberVerified === false && requests.length > 0;
  const showEnableNotificationsAlert =
    !(notificationsSettings?.push || notificationsSettings?.sms) && requests.length > 0;

  // Swing Cert Constants
  // Swing Cert applies to CA/NJ subs
  // eligibility based on state is checked on the backend
  const showNonSwingCertEmptyView =
    getFeatureFlags().SWING_CERT && hasSwingCert === false && isSwingCertRequestsAvailable;

  const showNonSwingCertCard =
    getFeatureFlags().SWING_CERT &&
    requests.length > 0 &&
    hasSwingCert === false &&
    isSwingCertRequestsAvailable &&
    isActivated;

  const refreshContainerRef = useRef<HTMLDivElement>(null);

  /********* Helpers ********/
  function handlePermanentlyHidingNonSwingCertCard() {
    window.localStorage.setItem("hideSwingCertCard", "true");
    setShouldHideSwingCertCard(true);
  }

  /********* Render ********/
  return (
    <>
      {Capacitor.getPlatform() !== "web" && (
        <PullToRefreshLoadingIndicator elementRef={refreshContainerRef} onRefresh={onRefresh} />
      )}
      <div
        ref={refreshContainerRef}
        css={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          css={{
            height: "calc(100% - 48px)",
            maxWidth: "900px",
          }}
        >
          <div css={{ margin: "0 auto" }}>
            {/* Alerts */}
            {showEnableNotificationsAlert && <EnableNotificationsAlert />}
            {showPhoneVerificationAlert && <PhoneVerificationAlert />}
            {confirmRequests && <ConfirmRequests confirmRequests={confirmRequests} />}

            {/* Updated label */}
            <p css={{ margin: 16, color: Colors.slate600, fontSize: 12, fontStyle: "italic" }}>
              {translate("LABEL_UPDATED", { min: timeSinceUpdate })}
            </p>

            {/* Swing Cert Card */}
            {showNonSwingCertCard && !shouldHideSwingCertCard && (
              <div css={{ margin: "16px 16px 24px" }}>
                <NonSwingCertCard
                  onClick={handlePermanentlyHidingNonSwingCertCard}
                  state={address?.state as "CA" | "NJ"}
                />
              </div>
            )}

            {/* Requests List or Empty View */}
            {requests.length ? (
              <div css={{ paddingBottom: 16 }}>
                {requests.map((req) => (
                  <div key={req.id} css={{ margin: "16px 16px 24px 16px" }}>
                    <OpeningsCard request={req} isPayClarityEnabled={isPayClarityEnabled} />
                  </div>
                ))}
                <span css={{ display: "flex", justifyContent: "center", fontStyle: "italic" }}>
                  {translate("LABEL_END_OF_PAGE")}!
                </span>
              </div>
            ) : showNonSwingCertEmptyView ? (
              <NonSwingCertEmptyView state={address?.state as "CA" | "NJ"} />
            ) : (
              <OpeningsEmptyView />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/********* Helper Components ********/
export function OpeningsEmptyView() {
  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--spacing-lg)",
        width: "100%",
        maxWidth: "440px",
        margin: "0 auto",
        // padding bottom to account for when confirm alert box is shown also
        padding: "104px 16px 32px 16px",
        textAlign: "center",
      }}
    >
      <img
        src="assets/man-with-large-magnifying-glass.svg"
        alt={translate("SETUP_ILLUSTRATION_IMAGE_ALT")}
        css={{ maxWidth: "200px" }}
      />
      <span css={{ fontSize: "var(--swing-font-md)", fontWeight: "var(--swing-font-semibold)" }}>
        {translate("OPENINGS_NONE_AVAILABLE")}
      </span>
      <span css={{ fontSize: "var(--swing-font-md)" }}>{translate("OPENINGS_RETURN_LATER")}.</span>
    </div>
  );
}

type ConfirmRequestsProps = {
  confirmRequests: PendingConfirmData[];
};

export function ConfirmRequests(props: ConfirmRequestsProps) {
  const { confirmRequests } = props;

  return confirmRequests.map((request) => {
    const formattedStartDate = DateTime.fromISO(request.startDate).toFormat("LLL dd");
    const formattedEndDate = DateTime.fromISO(request.endDate).toFormat("LLL dd");
    const formattedExpirationDate = DateTime.fromISO(request.confirmExpirationDate).toFormat(
      "LLL, dd",
    );
    const formattedExpirationTime = DateTime.fromISO(request.confirmExpirationDate)
      .toFormat("t")
      .toLowerCase();

    return (
      <PendingConfirmationBanner
        key={request.id}
        endDate={formattedEndDate}
        expirationDate={formattedExpirationDate}
        expirationTime={formattedExpirationTime}
        schoolName={request.schoolName}
        startDate={formattedStartDate}
        url={createRequestUrl(request.id)}
      />
    );
  });
}

type PendingConfirmationBannerProps = {
  endDate: string;
  expirationDate: string;
  expirationTime: string;
  schoolName: string;
  startDate: string;
  url: string;
};

function PendingConfirmationBanner(props: PendingConfirmationBannerProps) {
  const { endDate, expirationDate, expirationTime, schoolName, startDate, url } = props;
  return (
    <div css={{ margin: 16 }}>
      <Alert showIcon color="success" title={translate("CONFIRM_REQUEST_TITLE")}>
        <p css={{ margin: 0 }}>
          {translate("CONFIRM_REQUEST_MESSAGE_1", {
            schoolName,
            startDate,
            endDate,
          })}
          {", "}
          <Link to={url}>{translate("CONFIRM_REQUEST_MESSAGE_LINK_LABEL")}</Link>{" "}
          {translate("CONFIRM_REQUEST_MESSAGE_2")}{" "}
          <span css={{ fontWeight: "var(--swing-font-semibold)" }}>{expirationDate}</span>{" "}
          {translate("CONFIRM_REQUEST_MESSAGE_3")}{" "}
          <span css={{ fontWeight: "var(--swing-font-semibold)" }}>{expirationTime}</span>.
        </p>
      </Alert>
    </div>
  );
}

export function PhoneVerificationAlert() {
  return (
    <div css={{ margin: 16 }}>
      <Alert color="warning" title={translate("PHONE_VERIFICATION_ALERT_TITLE")} showIcon>
        <p css={{ margin: 0 }}>
          <Link to="/profile">{translate("PHONE_VERIFICATION_ALERT_MSG_LINK_LABEL")}</Link>
          {` ${translate("PHONE_VERIFICATION_ALERT_MSG")}`}
        </p>
      </Alert>
    </div>
  );
}

export function EnableNotificationsAlert() {
  // Check and remove this item if it exists now that
  // we would like to always display the alert if
  // notifications is turned off.
  useEffect(() => {
    if (localStorage.getItem("notificationsAlertDismissed")) {
      localStorage.removeItem("notificationsAlertDismissed");
    }
  }, []);

  return (
    <div css={{ margin: 16 }}>
      <Alert color="warning" title={translate("NEVER_MISS_AN_OPPORTUNITY_ALERT_TITLE")} showIcon>
        <p css={{ margin: 0 }}>
          <Link to="/more/notifications">{translate("NOTIFICATIONS_ALERT_MSG_LINK_LABEL")}</Link>
          {` ${translate("NOTIFICATIONS_ALERT_MSG")}`}
        </p>
      </Alert>
    </div>
  );
}
